import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, EmptyState, Skeleton, StarRating } from '@recipes/ui';
import { format } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { CalendarCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PageHeader } from '@/components/layout/page-header';
import { api } from '@/lib/api';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

/** /cooking-log: everything the user cooked, newest first. */
export function CookingLogPage() {
  const { t } = useTranslation();
  const dateLocale = currentLocale() === 'de' ? deLocale : enUS;
  const query = useInfiniteQuery({
    queryKey: queryKeys.cookingLog,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.ratings.cookingLog({ cursor: pageParam, limit: 30 }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const entries = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6" data-testid="cooking-log-page">
      <PageHeader
        title={t('recipes.cookingLog.pageTitle')}
        description={t('recipes.cookingLog.pageDescription')}
      />
      {query.isPending ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          title={t('app.unexpectedError')}
          action={
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('app.retry')}
            </Button>
          }
        />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck />}
          title={t('recipes.cookingLog.empty')}
          description={t('recipes.cookingLog.emptyHint')}
        />
      ) : (
        <>
          <ul className="divide-y rounded-lg border" data-testid="cooking-log-entries">
            {entries.map((entry) => (
              <li key={entry.id} className="flex gap-3 p-3">
                {entry.photoUrl && (
                  <img
                    src={entry.photoUrl}
                    alt=""
                    className="size-16 shrink-0 rounded object-cover"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/recipes/${entry.recipeId}`}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {entry.recipeTitle}
                  </Link>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    <time dateTime={entry.cookedAt}>
                      {format(new Date(entry.cookedAt), 'PPP', { locale: dateLocale })}
                    </time>
                    {entry.servings && (
                      <span>
                        {t('recipes.cookingLog.servingsValue', { count: Number(entry.servings) })}
                      </span>
                    )}
                    {entry.rating !== null && (
                      <StarRating
                        value={entry.rating}
                        readOnly
                        size="sm"
                        label={t('recipes.cookingLog.rating')}
                      />
                    )}
                  </div>
                  {entry.notes && <p className="mt-1 whitespace-pre-wrap text-sm">{entry.notes}</p>}
                </div>
              </li>
            ))}
          </ul>
          {query.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => void query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
